/**
 * Minimal i18n layer.
 *
 * Deliberately not react-i18next: with a single locale that library is
 * overhead, and swapping to it later is mechanical because every user-facing
 * string already lives in a dictionary rather than inline in JSX.
 *
 * To add a locale: drop in a second dictionary, set ACTIVE_LOCALE from user or
 * practice settings, and nothing else changes.
 */

import es from './es';

const DICTIONARIES = { es };

// Sourced from practice_settings.locale once settings load; this is the fallback.
export const DEFAULT_LOCALE = 'es-AR';
export const DEFAULT_TIMEZONE = 'America/Argentina/Buenos_Aires';
export const DEFAULT_CURRENCY = 'ARS';

let activeLocale = DEFAULT_LOCALE;
let activeTimezone = DEFAULT_TIMEZONE;
let activeCurrency = DEFAULT_CURRENCY;

export const configureLocale = ({ locale, timezone, currency } = {}) => {
  if (locale) activeLocale = locale;
  if (timezone) activeTimezone = timezone;
  if (currency) activeCurrency = currency;
};

/** The practice's timezone, for code that cuts days rather than formats them. */
export const practiceTimezone = () => activeTimezone;

const dictionaryFor = (locale) => DICTIONARIES[locale.split('-')[0]] || es;

/**
 * Look up a dotted key, e.g. t('appointments.status.in_session').
 * Falls back to the key itself so a missing string is visible rather than blank.
 * Interpolates {placeholders} from `vars`.
 */
export const t = (key, vars) => {
  let value = key
    .split('.')
    .reduce((node, part) => (node == null ? undefined : node[part]), dictionaryFor(activeLocale));

  // Pluralisation: a key may resolve to { one, other } and pick a form from
  // vars.count, so "1 registro" does not read as "1 registros".
  if (value && typeof value === 'object' && typeof value.other === 'string') {
    value = Number(vars?.count) === 1 ? value.one ?? value.other : value.other;
  }

  if (typeof value !== 'string') {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[i18n] missing string: ${key}`);
    }
    return key;
  }

  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
};

// --- formatting -------------------------------------------------------------
// Always via Intl, never hand-built, so a new region needs no format rewrites.

const DATE_PARTS = ['weekday', 'day', 'month', 'year', 'era', 'dateStyle'];

/**
 * Formats a date, defaulting to DD/MM/YYYY.
 *
 * Caller options REPLACE the default parts rather than merging into them:
 * asking for `{ weekday: 'short' }` must give "lun", not "lun, 31/08/2026".
 */
/** "1990-05-14" — a calendar date with no time, as Postgres DATE columns give. */
const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const formatDate = (value, options) => {
  if (!value) return '';
  const overridesParts = options && DATE_PARTS.some((part) => part in options);
  const parts = overridesParts
    ? options
    : { day: '2-digit', month: '2-digit', year: 'numeric', ...options };

  // A plain date is a calendar date, not an instant. It parses as UTC midnight,
  // so rendering it in a negative-offset zone would land on the previous day —
  // a date of birth of 1990-05-14 displaying as 13/05/1990. Read those in UTC
  // and leave real timestamps on the practice timezone.
  const timeZone = typeof value === 'string' && PLAIN_DATE.test(value) ? 'UTC' : activeTimezone;

  return new Intl.DateTimeFormat(activeLocale, { timeZone, ...parts }).format(new Date(value));
};

export const formatTime = (value, options) => {
  if (!value) return '';
  return new Intl.DateTimeFormat(activeLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: activeTimezone,
    ...options,
  }).format(new Date(value));
};

export const formatDateTime = (value) =>
  value ? `${formatDate(value)} ${formatTime(value)}` : '';

export const formatMoney = (amount) =>
  new Intl.NumberFormat(activeLocale, {
    style: 'currency',
    currency: activeCurrency,
    minimumFractionDigits: 2,
  }).format(Number(amount ?? 0));

/** Long weekday + day, for calendar column headers. */
export const formatWeekdayLabel = (value) =>
  new Intl.DateTimeFormat(activeLocale, {
    weekday: 'long',
    day: 'numeric',
    timeZone: activeTimezone,
  }).format(new Date(value));

/** Whole-minute duration between two instants, for waiting/session metrics. */
export const durationMinutes = (from, to) => {
  if (!from || !to) return null;
  return Math.max(0, Math.round((new Date(to) - new Date(from)) / 60000));
};

export const formatDuration = (minutes) => {
  if (minutes == null) return '—';
  // A session that ran under a minute is real (someone mis-clicked, or a test);
  // reporting it as "0 min" reads like nothing was recorded at all.
  if (minutes < 1) return t('common.underAMinute');
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) return t('common.minutesShort', { count: mins });
  return t('common.hoursMinutesShort', { hours, minutes: mins });
};

/** Age in whole years at a given moment — patient age at time of session. */
export const ageAt = (dateOfBirth, at = new Date()) => {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  const on = new Date(at);
  let age = on.getFullYear() - birth.getFullYear();
  const monthDelta = on.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && on.getDate() < birth.getDate())) age -= 1;
  return age;
};
